import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import { MdSearch, MdOpenInNew } from 'react-icons/md';
import { fetchActiveWarranties } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import VenuesListSkeleton from '../../common/skeletons/VenuesListSkeleton'; // Reusing skeleton or general one

const WarrantiesList = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [warranties, setWarranties] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, total: 0, per_page: 10, last_page: 1 });

    useEffect(() => {
        fetchWarranties();
    }, [pagination.current_page, appliedSearch, sortBy, sortDirection]);

    const fetchWarranties = async (search = '') => {
        setLoading(true);
        try {
            const response = await fetchActiveWarranties({
                page: pagination.current_page,
                perPage: pagination.per_page,
                searchTerm: search,
                sortBy,
                sortDirection
            });
            setWarranties(response.data);
            setPagination({
                current_page: response.current_page,
                total: response.total,
                per_page: response.per_page,
                last_page: response.last_page
            });
        } catch (error) {
            console.error('Error fetching active warranties:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchClick = () => {
        setPagination(prev => ({ ...prev, current_page: 1 }));
        setAppliedSearch(searchTerm.trim());
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const handleSort = (column, direction) => {
        const field = column.sortField || '';
        setSortBy(field);
        setSortDirection(direction);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const columns = useMemo(() => [
        {
            name: t('Warranties.serialNumber'),
            selector: row => row.serial_number || '-',
            sortable: true,
            sortField: 'serial_number',
        },
        {
            name: t('Warranties.item'),
            selector: row => row.item?.manufacturer_item?.product_name || '-',
            cell: row => (
                <ItemCell>
                    <span>{row.item?.manufacturer_item?.product_name || '-'}</span>
                    {row.item_id && (
                        <ItemLink to={`/items/view/${row.item_id}`}>
                            <MdOpenInNew />
                            {t('Warranties.viewItem')}
                        </ItemLink>
                    )}
                </ItemCell>
            ),
        },
        {
            name: t('Warranties.activationDate'),
            selector: row => row.start_date ? new Date(row.start_date).toLocaleDateString() : '-',
            sortable: true,
            sortField: 'start_date',
        },
        {
            name: t('Warranties.expirationDate'),
            selector: row => row.end_date ? new Date(row.end_date).toLocaleDateString() : '-',
            sortable: true,
            sortField: 'end_date',
        },
        {
            name: t('Warranties.buyer'),
            selector: row => `${row.buyer?.name ?? ''} ${row.buyer?.surname ?? ''}`.trim() || '-',
        },
        {
            name: t('Warranties.status'),
            selector: row => row.status || '-',
            sortable: true,
            sortField: 'status',
        },
        {
            name: t('Warranties.issuer'),
            selector: row => row.issuer?.name || '-',
        }
    ], [t]);

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                },
            },
        },
    };

    return (
        <StyledCard noPadding>
            <ControlsContainer>
                <SearchContainer>
                    <MdSearch size={20} />
                    <input
                        type="text"
                        placeholder={t('Warranties.enterSerialNumber')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
                    />
                </SearchContainer>
                <MainButton size="small" onClick={handleSearchClick} variant="primary">
                    {t('Warranties.searchWarranties')}
                </MainButton>
            </ControlsContainer>

            {loading ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                    {t('Warranties.loading')}
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={warranties}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.total}
                    paginationPerPage={pagination.per_page}
                    paginationComponentOptions={{
                        rowsPerPageText: t('Warranties.rowsPerPage'),
                        rangeSeparatorText: t('Warranties.of'),
                        noRowsPerPage: true
                    }}
                    onChangePage={handlePageChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    noHeader
                    customStyles={customStyles}
                    noDataComponent={<EmptyState>{t('Warranties.noWarranties')}</EmptyState>}
                />
            )}
        </StyledCard>
    );
};

export default WarrantiesList;

// Styled Components
const ControlsContainer = styled.div`
    padding: 1rem;
    display: flex;
    gap: 1rem;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    flex: 1;
    max-width: 400px;

    svg {
        color: #9ca3af;
        margin-right: 0.5rem;
    }

    input {
        border: none;
        background: transparent;
        outline: none;
        font-size: 0.875rem;
        color: #111827;
        width: 100%;

        &::placeholder {
            color: #9ca3af;
        }
    }
    
    &:focus-within {
        border-color: #701a82;
        box-shadow: 0 0 0 1px #701a82;
    }
`;

const ItemCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const ItemLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #701a82;
    text-decoration: none;

    svg {
        font-size: 0.875rem;
    }

    &:hover {
        text-decoration: underline;
    }
`;

const EmptyState = styled.div`
    padding: 3rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
`;
